import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Map as MapIcon, Play } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { ChoroplethMap, type ChoroplethValue } from '@/shared/map/ChoroplethMap'
import { UnitPicker } from '@/features/incidents/wizard/UnitPicker'
import {
  fanOut, fanOutEstimate, getRun, listRuns, reportForRun, runChildren,
  type FanOutEstimate, type Run,
} from './api'
import { RUN_STATUS_LABEL, isLiveRun, usd } from './vocab'

const CHILD_TYPES = [
  { value: 'CONSTITUENCY', label: 'Constituencies' },
  { value: 'WARD', label: 'Wards' },
  { value: 'POLLING_STATION', label: 'Polling stations' },
]

/**
 * Score every unit in a scope, then paint the map.
 *
 * ## Why this is metered before it is offered
 *
 * A fan-out is N evidence briefs and N councils — one per unit. Forty wards is
 * forty runs. So the estimate comes first, with the unit count and a cost, and
 * the button is not enabled until somebody has seen it. The alternative is that
 * the first anybody hears of “every polling station in the county” is the budget
 * alert.
 *
 * ## The per-unit council is deliberately cheap
 *
 * Children run the SCAN council — one economy model and an editor with small
 * ceilings — not the full four-model council. Forty full councils to colour a map
 * would cost more than the map is worth, and the per-unit question (“score this
 * unit for X”) is genuinely simpler than the parent's.
 *
 * ## A unit with no data is grey, never the middle of the scale
 *
 * The scan playbook is told to return NULL rather than 50 when a unit is too
 * thin to judge, and the map paints those in the no-data fill and counts them
 * under the legend. A map that renders missing data as average is the most
 * confidently wrong thing this module could produce.
 */
export function MapPage() {
  const navigate = useNavigate()
  const [parentUnitId, setParentUnitId] = useState<string | null>(null)
  const [childType, setChildType] = useState('WARD')
  const [question, setQuestion] = useState('risk to turnout on polling day')
  const [estimate, setEstimate] = useState<FanOutEstimate | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [parents, setParents] = useState<Run[]>([])
  const [selected, setSelected] = useState<Run | null>(null)
  const [values, setValues] = useState<ChoroplethValue[]>([])
  const [loadingMap, setLoadingMap] = useState(false)
  const mayRun = useCan('advisor:run')

  const loadParents = useCallback(async () => {
    try {
      const runs = await listRuns(0, 50)
      setParents(runs.filter((r) => r.childCount > 0))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void loadParents() }, [loadParents])

  useEffect(() => {
    if (!parentUnitId) { setEstimate(null); return }
    let alive = true
    fanOutEstimate(parentUnitId, childType)
      .then((e) => { if (alive) setEstimate(e) })
      .catch((err) => { if (alive) setError(explain(err)) })
    return () => { alive = false }
  }, [parentUnitId, childType])

  /**
   * Reads the score out of each child's report.
   *
   * The number comes from the child's first finding's `geoMetric` — the scan
   * playbook is instructed to put its 0-100 there. A child with no finding, or a
   * finding with no metric, contributes a NULL and shows as no-data rather than
   * as a zero.
   */
  const openParent = async (run: Run) => {
    setSelected(run)
    setLoadingMap(true)
    setValues([])
    try {
      const children = await runChildren(run.id)
      const scored = await Promise.all(children.map(async (child) => {
        if (!child.reportId) {
          return { unitId: child.scopeRef ?? '', label: child.scopeLabel, value: null }
        }
        try {
          const report = await reportForRun(child.id)
          const metric = report.findings.find((f) => f.geoMetric != null)?.geoMetric ?? null
          return { unitId: child.scopeRef ?? '', label: child.scopeLabel, value: metric }
        } catch {
          return { unitId: child.scopeRef ?? '', label: child.scopeLabel, value: null }
        }
      }))
      setValues(scored.filter((v) => v.unitId))
    } catch (err) {
      setError(explain(err))
    } finally {
      setLoadingMap(false)
    }
  }

  const start = async () => {
    if (!parentUnitId) return
    setBusy(true)
    setError(null)
    try {
      const parent = await fanOut({
        playbookCode: 'UNIT_SCAN',
        parentUnitId,
        childUnitType: childType,
        question: question.trim() || undefined,
      })
      await loadParents()
      await openParent(await getRun(parent.id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const pending = selected
    ? values.length === 0 || values.every((v) => v.value == null)
    : false

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-erp-lg font-semibold">Advisory map</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          One council per unit, then the answers painted. Expensive by construction — the
          estimate below is what it will cost before anything starts.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      <section className="rounded-md border border-border p-3 space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-erp-xs text-muted-foreground">Across which area?</label>
            <UnitPicker value={parentUnitId} onPick={(u) => setParentUnitId(u?.id ?? null)} />
          </div>
          <div className="space-y-2">
            <div>
              <label className="text-erp-xs text-muted-foreground">Scored at which level?</label>
              <select value={childType} onChange={(e) => setChildType(e.target.value)}
                className="erp-input w-full text-erp-sm">
                {CHILD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-erp-xs text-muted-foreground">Scored for what?</label>
              <input value={question} onChange={(e) => setQuestion(e.target.value)}
                className="erp-input w-full text-erp-sm" />
            </div>
          </div>
        </div>

        {estimate && (
          <Note tone={estimate.unitCount > 60 ? 'bad' : 'info'}>
            <span className="font-medium">
              {estimate.unitCount} units → {estimate.unitCount} runs, about{' '}
              {usd(estimate.estimatedCostUsd, 2)}.
            </span>{' '}
            {estimate.sampleUnits.slice(0, 5).join(', ')}
            {estimate.sampleUnits.length > 5 ? '…' : ''}
            {estimate.capped && (
              <> Capped at {estimate.cap}: only the first {estimate.unitCount} will run, and the
                rest will have no data on the map rather than a wrong one.</>
            )}
          </Note>
        )}

        <div className="flex justify-end">
          <button type="button"
            disabled={busy || !mayRun || !parentUnitId || !estimate || estimate.unitCount === 0}
            onClick={() => void start()}
            className="erp-btn erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center
              gap-2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Play className="h-3.5 w-3.5" />}
            {mayRun ? 'Run and paint the map' : 'Needs advisor:run'}
          </button>
        </div>
      </section>

      {parents.length > 0 && (
        <section>
          <h2 className="text-erp-sm font-medium mb-1.5">Previous fan-outs</h2>
          <ul className="flex flex-wrap gap-1.5">
            {parents.map((run) => (
              <li key={run.id}>
                <button type="button" onClick={() => void openParent(run)}
                  className={cnBtn(selected?.id === run.id)}>
                  <MapIcon className="h-3 w-3" />
                  {run.scopeLabel} · {run.childCount} units
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {selected && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-erp-sm font-medium">{selected.title}</h2>
            <span className="text-erp-xs text-muted-foreground">
              {RUN_STATUS_LABEL[selected.status] ?? selected.status}
              {isLiveRun(selected.status) && ' — units are still being scored'}
            </span>
          </div>

          {loadingMap && <LoadingBlock label="Reading each unit's report…" />}

          {!loadingMap && pending && (
            <Note tone="info">
              No unit has a score yet. Each one is its own council run; the map fills in as they
              finish, and a unit whose data was too thin to judge stays grey rather than being
              given a number.
            </Note>
          )}

          {!loadingMap && !pending && (
            <ChoroplethMap
              unitType={childType}
              parentUnitId={selected.scopeRef}
              values={values}
              valueLabel={selected.question ?? 'score'}
              onSelect={(unitId) => {
                const child = values.find((v) => v.unitId === unitId)
                if (child) {
                  void runChildren(selected.id).then((children) => {
                    const match = children.find((c) => c.scopeRef === unitId)
                    if (match) navigate(`/advisory/runs/${match.id}`)
                  })
                }
              }}
              height="h-[520px]" />
          )}
        </section>
      )}
    </div>
  )
}

function cnBtn(active: boolean) {
  return [
    'erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5',
    active ? 'border-chocolate text-chocolate' : '',
  ].join(' ')
}
